import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Meeting } from '@/services/meeting';

interface MeetingState {
	meetings: Meeting[];
	lastSyncTime: number;
	/** Meeting id for which the details sheet is open; null when closed. Not persisted. */
	selectedMeetingId: number | null;
	/**
	 * Meeting being edited via the create-meeting dialog. Set from the details
	 * sheet's Edit button OR from the meeting card's right-click context menu.
	 * Not persisted — gets cleared on dialog close.
	 */
	meetingToEdit: Meeting | null;
	setMeetings: (meetings: Meeting[]) => void;
	setLastSyncTime: (t: number) => void;
	setSelectedMeetingId: (id: number | null) => void;
	setMeetingToEdit: (meeting: Meeting | null) => void;
	mergeMeetings: (delta: Meeting[]) => void;
	clearMeetings: () => void;
}

export const useMeetingStore = create<MeetingState>()(
	persist(
		(set) => ({
			meetings: [],
			lastSyncTime: 0,
			selectedMeetingId: null,
			meetingToEdit: null,

			setMeetings: (meetings) => set({ meetings }),

			setLastSyncTime: (t) => set({ lastSyncTime: t }),

			setSelectedMeetingId: (id) => set({ selectedMeetingId: id }),

			setMeetingToEdit: (meeting) => set({ meetingToEdit: meeting }),

			mergeMeetings: (delta) =>
				set((state) => {
					const byId = new Map(state.meetings.map((m) => [m.id, m]));
					for (const m of delta) {
						if (m.is_delete === '1') {
							byId.delete(m.id);
						} else {
							const prev = byId.get(m.id);
							if (prev) {
								// Partial merge: socket envelopes sometimes omit nested
								// objects (e.g. status-update broadcasts only carry the
								// status fields). Preserve previous values when the
								// delta doesn't include them so creator/receiver
								// avatars don't blank out on accept-reject events.
								byId.set(m.id, {
									...prev,
									...m,
									tag: m.tag ?? prev.tag,
									creator: m.creator ?? prev.creator,
									receiver: m.receiver ?? prev.receiver,
									group: m.group ?? prev.group,
									company: m.company ?? prev.company,
									members: m.members ?? prev.members,
									files: m.files ?? prev.files,
									checklist: m.checklist ?? prev.checklist,
								});
							} else {
								byId.set(m.id, m);
							}
						}
					}
					return { meetings: Array.from(byId.values()) };
				}),

			clearMeetings: () => set({ meetings: [], lastSyncTime: 0 }),
		}),
		{
			name: 'meeting-storage',
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({ meetings: state.meetings, lastSyncTime: state.lastSyncTime }),
		}
	)
);

/** Store instance for use outside React (e.g. in queryFn). */
export const meetingStore = useMeetingStore;
